/**
 * <s-sticky-buy> — Floating add to cart bar for the product page. Appears once the
 * shopper scrolls past the main add to cart form and submits that form.
 *
 * Required markup:
 *   data-form-id                      — id of the main product <form>; the bar's button uses form="…".
 *   [data-sticky-button] inside       — type="submit" name="add" button tied to that form.
 *   [data-sticky-variant] inside      — Optional text node mirroring the selected variant title.
 *   Main form region: s-product-form[data-product-form] (swapped by variant changes). Its
 *   button[name="add"] label is mirrored; when sold out, the [data-restock-button] label is
 *   mirrored instead and tapping the bar opens its back-in-stock sign-up. With neither, the bar button is disabled.
 *   [data-variant-title] inside the region carries the selected variant title.
 * Sets [data-visible] and toggles inert on itself.
 */
class SStickyBuy extends HTMLElement {
  #observer = null;
  #mutations = null;
  #controller = null;

  connectedCallback() {
    const region = document.querySelector('s-product-form[data-product-form]');
    const button = this.querySelector('[data-sticky-button]');
    if (!region || !button) return;
    this.#controller = new AbortController();
    this.#setVisible(false);

    // Watch the real add to cart button itself: the bar appears as soon as it scrolls above the screen.
    let watched = null;
    this.#observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      this.#setVisible(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
    });
    const watch = () => {
      const target = region.querySelector('form button[name="add"], form button[data-restock-button], form button[aria-disabled="true"], form button[disabled]') ?? region;
      if (target === watched) return;
      if (watched) this.#observer.unobserve(watched);
      watched = target;
      this.#observer.observe(target);
    };
    watch();

    // Variant picker in the bar: hands the chosen variant to the main <s-variant-picker>.
    const select = this.querySelector('[data-sticky-select]');
    select?.addEventListener('change', () => {
      const picker = region.querySelector('s-variant-picker');
      if (!picker || !select.value) return;
      const proxy = document.createElement('input');
      proxy.type = 'hidden';
      proxy.dataset.variantId = select.value;
      picker.append(proxy);
      proxy.dispatchEvent(new Event('change', { bubbles: true }));
      proxy.remove();
    }, { signal: this.#controller.signal });

    const sync = () => {
      watch();
      const idInput = region.querySelector('input[name="id"]');
      if (select && idInput && select.value !== idInput.value && document.activeElement !== select) select.value = idInput.value;
      // Thumbnail follows the selected variant's photo (e.g. picking a color shows that color).
      const image = this.querySelector('[data-sticky-image]');
      const option = select?.querySelector(`option[value="${idInput?.value || select.value}"]`) ?? select?.selectedOptions?.[0];
      const src = option?.dataset.image;
      if (image && src && image.getAttribute('src') !== src) {
        image.removeAttribute('srcset');
        image.src = src;
      }
      const main = region.querySelector('button[name="add"]');
      const restock = region.querySelector('form button[data-restock-button]');
      const fallback = region.querySelector('form button[aria-disabled="true"]');
      const source = main ?? restock ?? fallback;
      const label = button.querySelector('[data-sticky-label]') ?? button;
      if (source) label.textContent = source.textContent.trim();
      button.toggleAttribute('data-restock', !main && !!restock);
      const disabled = !main && !restock;
      button.toggleAttribute('data-disabled', disabled);
      if (disabled) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      const variantTarget = this.querySelector('[data-sticky-variant]');
      const variantSource = region.querySelector('[data-variant-title]');
      if (variantTarget) variantTarget.textContent = variantSource ? variantSource.textContent.trim() : '';
    };
    sync();
    this.#mutations = new MutationObserver(sync);
    this.#mutations.observe(region, { childList: true, subtree: true });

    button.addEventListener('click', (event) => {
      if (button.hasAttribute('data-disabled')) {
        event.preventDefault();
        return;
      }
      // Sold out: the bar button opens the same "Notify me when it's back" sign-up as the main button.
      if (button.hasAttribute('data-restock')) {
        event.preventDefault();
        region.querySelector('form button[data-restock-button]')?.click();
      }
    }, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    this.#controller?.abort();
  }

  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', SStickyBuy);
